import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PassCard, Button } from '@sundkortid/ui';
import type { PassSummary } from '@sundkortid/shared';
import { api, ApiRequestError } from '../../api/client';

export function PassesPage() {
  const [passes, setPasses] = useState<PassSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<PassSummary[]>('/passes')
      .then(setPasses)
      .catch((err) =>
        setError(err instanceof ApiRequestError ? err.message : 'Villa við að sækja kort'),
      )
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <div>
      {/* Header */}
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-primary-400 mb-2">
            Mín kort
          </p>
          <h1 className="text-3xl font-title font-bold text-primary-900">Kortin þín</h1>
        </div>
        <Link to="/buy">
          <Button variant="outline" size="sm">+ Nýtt kort</Button>
        </Link>
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[1, 2].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 h-48 animate-pulse" />
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && passes.length === 0 && (
        <div className="text-center py-16">
          <div className="w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-primary-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
            </svg>
          </div>
          <h3 className="text-lg font-title font-bold text-primary-900 mb-2">Engin kort</h3>
          <p className="text-sm text-gray-400 mb-6 max-w-xs mx-auto">
            Kauptu fyrsta sundkortið þitt og byrjaðu að nota sundlaugarnar
          </p>
          <Link to="/buy">
            <Button>Kaupa kort</Button>
          </Link>
        </div>
      )}

      {/* Pass grid */}
      {!isLoading && !error && passes.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 stagger-children">
          {passes.map((pass) => (
            <PassCard key={pass.id} pass={pass} />
          ))}
        </div>
      )}
    </div>
  );
}
